import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { BillingProfileWriteBackService } from './billing-profile-writeback.service';
import {
  CreateSchoolReq,
  UpdateRootSchoolReq,
  UpdateSchoolInfoReq,
  UpdateSchoolBasicInfoReq,
  UpdateAboutUsReq,
  UpdateWhatWeOfferReq,
  UpdateEducationalFocusReq,
  UpdateGalleryItemReq,
  ReorderGalleryReq,
  UpdateDocTitleReq,
  UpdateSchoolMembershipsReq,
  UpdateSchoolContactReq,
  SwapSubSchoolOrderReq,
  SchoolAuthorityTree,
  SchoolContactDetail,
  SchoolUnit,
  SchoolDetailExtended,
  SchoolPermissionApiResponse,
  SchoolSummaryList,
  SchoolTreeList,
  GetSchoolsParams,
  UserSchoolItem,
  IdRes,
  StatusRes,
  PermissionTypeEnum,
  MySchoolPermissionsRes,
} from '../types/rest-api';

/**
 * Service for School-domain REST API endpoints.
 * Maps to OpenAPI tag: School
 * Base path: /v1/schools
 */
@Injectable({
  providedIn: 'root',
})
export class SchoolApiService {
  private readonly http = inject(HttpClient);
  private readonly billingWriteBack = inject(BillingProfileWriteBackService);
  private readonly baseUrl = `${environment.apiBaseUrl}/v1/schools`;

  // POST /v1/schools/create
  createSchool(body: CreateSchoolReq) {
    return this.http.post<IdRes>(`${this.baseUrl}/create`, body).pipe(
      // The order that paid for this school was placed before the school
      // existed, so its invoice address had nowhere to go. The billing account
      // is created together with the school -- this is the first moment it can
      // be stored.
      tap(
        res =>
          void this.billingWriteBack.flushPendingFor(
            body.school_authority_id
              ? { kind: 'authority', id: body.school_authority_id }
              : { kind: 'school', id: res.id }
          )
      )
    );
  }

  // PUT /v1/schools/{school_id}/root
  updateRootSchool(schoolId: string, body: UpdateRootSchoolReq) {
    return this.http.put<StatusRes>(`${this.baseUrl}/${schoolId}/root`, body);
  }

  // PUT /v1/schools/{school_id}/swap-sub-school-order
  swapSubSchoolOrder(schoolId: string, body: SwapSubSchoolOrderReq) {
    return this.http.put<StatusRes>(
      `${this.baseUrl}/${schoolId}/swap-sub-school-order`,
      body
    );
  }

  // DELETE /v1/schools/{school_id}
  deleteSchool(schoolId: string) {
    return this.http.delete<StatusRes>(`${this.baseUrl}/${schoolId}`);
  }

  // POST /v1/schools/{school_id}/profile-picture
  uploadProfilePicture(schoolId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<StatusRes>(
      `${this.baseUrl}/${schoolId}/profile-picture`,
      formData
    );
  }

  // DELETE /v1/schools/{school_id}/profile-picture
  deleteProfilePicture(schoolId: string) {
    return this.http.delete<StatusRes>(
      `${this.baseUrl}/${schoolId}/profile-picture`
    );
  }

  // POST /v1/schools/{school_id}/wall-picture
  uploadWallPicture(schoolId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<StatusRes>(
      `${this.baseUrl}/${schoolId}/wall-picture`,
      formData
    );
  }

  // DELETE /v1/schools/{school_id}/wall-picture
  deleteWallPicture(schoolId: string) {
    return this.http.delete<StatusRes>(
      `${this.baseUrl}/${schoolId}/wall-picture`
    );
  }

  // POST /v1/schools/{school_id}/video/upload-token
  requestVideoUploadToken(schoolId: string) {
    return this.http.post<StatusRes>(
      `${this.baseUrl}/${schoolId}/video/upload-token`,
      {}
    );
  }

  // POST /v1/schools/{school_id}/video/{api_video_id}
  uploadVideo(schoolId: string, apiVideoId: string) {
    return this.http.post<StatusRes>(
      `${this.baseUrl}/${schoolId}/video/${apiVideoId}`,
      {}
    );
  }

  // DELETE /v1/schools/{school_id}/video
  deleteVideo(schoolId: string) {
    return this.http.delete<StatusRes>(`${this.baseUrl}/${schoolId}/video`);
  }

  // PUT /v1/schools/{school_id}/info
  updateInfo(schoolId: string, body: UpdateSchoolInfoReq) {
    return this.http.put<StatusRes>(`${this.baseUrl}/${schoolId}/info`, body);
  }

  // PUT /v1/schools/{school_id}/basic-info
  updateBasicInfo(schoolId: string, body: UpdateSchoolBasicInfoReq) {
    return this.http.put<StatusRes>(
      `${this.baseUrl}/${schoolId}/basic-info`,
      body
    );
  }

  // PUT /v1/schools/{school_id}/about-us
  updateAboutUs(schoolId: string, body: UpdateAboutUsReq) {
    return this.http.put<StatusRes>(
      `${this.baseUrl}/${schoolId}/about-us`,
      body
    );
  }

  // PUT /v1/schools/{school_id}/what-we-offer
  updateWhatWeOffer(schoolId: string, body: UpdateWhatWeOfferReq) {
    return this.http.put<StatusRes>(
      `${this.baseUrl}/${schoolId}/what-we-offer`,
      body
    );
  }

  // PUT /v1/schools/{school_id}/educational-focus
  updateEducationalFocus(schoolId: string, body: UpdateEducationalFocusReq) {
    return this.http.put<StatusRes>(
      `${this.baseUrl}/${schoolId}/educational-focus`,
      body
    );
  }

  // PUT /v1/schools/{school_id}/publish
  publishSchool(schoolId: string) {
    return this.http.put<StatusRes>(`${this.baseUrl}/${schoolId}/publish`, {});
  }

  // POST /v1/schools/{school_id}/gallery
  uploadGalleryItem(
    schoolId: string,
    fileImage: File,
    opts?: { fileAudio?: File; title?: string; description?: string }
  ) {
    const formData = new FormData();
    formData.append('file_image', fileImage);
    if (opts?.fileAudio) formData.append('file_audio', opts.fileAudio);
    if (opts?.title) formData.append('title', opts.title);
    if (opts?.description) formData.append('description', opts.description);
    return this.http.post<IdRes>(
      `${this.baseUrl}/${schoolId}/gallery`,
      formData
    );
  }

  // PUT /v1/schools/{school_id}/gallery/{gallery_id}
  updateGalleryItem(
    schoolId: string,
    galleryId: string,
    body: UpdateGalleryItemReq
  ) {
    return this.http.put<StatusRes>(
      `${this.baseUrl}/${schoolId}/gallery/${galleryId}`,
      body
    );
  }

  // DELETE /v1/schools/{school_id}/gallery/{gallery_id}
  deleteGalleryItem(schoolId: string, galleryId: string) {
    return this.http.delete<StatusRes>(
      `${this.baseUrl}/${schoolId}/gallery/${galleryId}`
    );
  }

  // PUT /v1/schools/{school_id}/gallery/reorder
  reorderGallery(schoolId: string, body: ReorderGalleryReq) {
    return this.http.put<StatusRes>(
      `${this.baseUrl}/${schoolId}/gallery/reorder`,
      body
    );
  }

  // POST /v1/schools/{school_id}/docs
  uploadDoc(schoolId: string, file: File, title: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    return this.http.post<IdRes>(`${this.baseUrl}/${schoolId}/docs`, formData);
  }

  // PUT /v1/schools/{school_id}/docs/{school_doc_id}
  updateDocTitle(schoolId: string, docId: string, body: UpdateDocTitleReq) {
    return this.http.put<StatusRes>(
      `${this.baseUrl}/${schoolId}/docs/${docId}`,
      body
    );
  }

  // DELETE /v1/schools/{school_id}/docs/{school_doc_id}
  deleteDoc(schoolId: string, docId: string) {
    return this.http.delete<StatusRes>(
      `${this.baseUrl}/${schoolId}/docs/${docId}`
    );
  }

  // PUT /v1/schools/memberships
  updateMemberships(body: UpdateSchoolMembershipsReq) {
    return this.http.put<StatusRes>(`${this.baseUrl}/memberships`, body);
  }

  // PUT /v1/schools/contacts
  updateContacts(body: UpdateSchoolContactReq) {
    return this.http.put<StatusRes>(`${this.baseUrl}/contacts`, body);
  }

  // POST /v1/permissions/schools/{school_id}/admins/{user_id}
  addAdmin(
    schoolId: string,
    userId: string,
    role: PermissionTypeEnum = 'ADMIN'
  ) {
    return this.http.post<StatusRes>(
      `${environment.apiBaseUrl}/v1/permissions/schools/${schoolId}/admins/${userId}`,
      { role }
    );
  }

  // PATCH /v1/permissions/schools/{school_id}/admins/{user_id}
  updateAdminRole(schoolId: string, userId: string, role: PermissionTypeEnum) {
    return this.http.patch<StatusRes>(
      `${environment.apiBaseUrl}/v1/permissions/schools/${schoolId}/admins/${userId}`,
      { role }
    );
  }

  // GET /v1/permissions/schools/{school_id}/my-role
  getMySchoolPermissions(schoolId: string) {
    return this.http.get<MySchoolPermissionsRes>(
      `${environment.apiBaseUrl}/v1/permissions/schools/${schoolId}/my-role`
    );
  }

  // DELETE /v1/permissions/schools/{school_id}/admins/{user_id}
  removeAdmin(schoolId: string, userId: string) {
    return this.http.delete<StatusRes>(
      `${environment.apiBaseUrl}/v1/permissions/schools/${schoolId}/admins/${userId}`
    );
  }

  // DELETE /v1/schools/{school_id}/members/{user_id}
  removeMember(schoolId: string, userId: string) {
    return this.http.delete<StatusRes>(
      `${this.baseUrl}/${schoolId}/members/${userId}`
    );
  }

  // GET /v1/schools/{school_id}/unit
  getUnit(schoolId: string) {
    return this.http.get<SchoolUnit>(`${this.baseUrl}/${schoolId}/unit`);
  }

  // GET /v1/schools/{school_id}/authority-tree
  getAuthorityTree(schoolId: string) {
    return this.http.get<SchoolAuthorityTree>(
      `${this.baseUrl}/${schoolId}/authority-tree`
    );
  }

  // GET /v1/schools/{school_id}/permissions
  getPermissions(schoolId: string) {
    return this.http.get<SchoolPermissionApiResponse>(
      `${this.baseUrl}/${schoolId}/permissions`
    );
  }

  // GET /v1/schools/{school_id}/detail
  getDetail(schoolId: string) {
    return this.http.get<SchoolDetailExtended>(
      `${this.baseUrl}/${schoolId}/detail`
    );
  }

  // GET /v1/schools/{school_id}/contacts
  getContacts(schoolId: string) {
    return this.http.get<SchoolContactDetail[]>(
      `${this.baseUrl}/${schoolId}/contacts`
    );
  }

  // GET /v1/schools/my-schools
  getMySchools() {
    return this.http.get<UserSchoolItem[]>(`${this.baseUrl}/my-schools`);
  }

  // GET /v1/schools
  getSchools(params?: GetSchoolsParams) {
    let httpParams = new HttpParams();
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            for (const item of value) {
              httpParams = httpParams.append(key, String(item));
            }
          } else {
            httpParams = httpParams.set(key, String(value));
          }
        }
      }
    }
    return this.http
      .get<SchoolTreeList>(this.baseUrl, { params: httpParams })
      .pipe(
        map(body => ({
          schools: body.items ?? [],
          page: body.page,
          page_size: body.page_size,
          total_count: body.total_count,
          display_count: body.display_count,
        }))
      );
  }
}
